import type { Locale } from "locale-enum";
import { Box, HStack, Pressable, Spinner, Text, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";

import { getLocalizedDay } from "../helpers/generalHelpers";
import { shouldHaveAccessToGenerateMeals } from "../helpers/userHelpers";
import type {
  CalendarDay,
  NutritionDayPlan,
  Organisation,
  PaginatedCalendarDayList,
  User,
  WeeklyNutritionPlan,
} from "../services/backendTypes";

interface EmptyDayActionsProps {
  user: User | null;
  organisation: Organisation | undefined;
  onPressChooseForMe: () => Promise<void>;
  onPressDoItMyself: () => Promise<void>;
  onPressCopyFromDate: () => Promise<void>;
  isLoadingChooseForMe: boolean;
  isLoadingDoItMyself: boolean;
  isLoadingCopyFromDate: boolean;
  lastDateThereAreMealsPlannedWithThisNutritionDayPlan: CalendarDay | null | undefined;
  userLocaleFromStore: Locale | undefined;
  calendarDayListResponse: PaginatedCalendarDayList | undefined;
  weeklyNutritionPlan: WeeklyNutritionPlan | null | undefined;
  nutritionPlanForCurrentDay: NutritionDayPlan | undefined;
}

const EmptyDayActions = ({
  user,
  organisation,
  onPressChooseForMe,
  onPressDoItMyself,
  onPressCopyFromDate,
  isLoadingChooseForMe,
  isLoadingDoItMyself,
  isLoadingCopyFromDate,
  lastDateThereAreMealsPlannedWithThisNutritionDayPlan,
  userLocaleFromStore,
  calendarDayListResponse,
  weeklyNutritionPlan,
  nutritionPlanForCurrentDay,
}: EmptyDayActionsProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();

  const buttonStyle = {
    paddingVertical: 12,
    paddingHorizontal: 12,
    alignItems: "center" as const,
  };

  const textStyle = {
    fontSize: 18,
    fontFamily: theme.fonts.body,
    fontWeight: "600" as const,
    color: "gray.700",
    borderBottomWidth: 0.5,
    borderBottomColor: "gray.400",
    paddingBottom: 4,
    paddingHorizontal: 8,
  };

  return (
    <Box flex={1} justifyContent="center" alignItems="center" px={4} mt={20}>
      <VStack space={3} alignItems="center">
        {shouldHaveAccessToGenerateMeals(organisation) ? (
          <Pressable
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onPress={onPressChooseForMe}
            disabled={isLoadingChooseForMe || !user || !calendarDayListResponse || !weeklyNutritionPlan}
            style={buttonStyle}
            _pressed={{ opacity: 0.7 }}
            testID="choose-for-me-button"
          >
            <HStack space={2} alignItems="center">
              {isLoadingChooseForMe ? <Spinner size="sm" color="gray.700" /> : null}
              <Text style={textStyle} textAlign="center">
                {t("planner.auto_generate_meals_button_text").replace("!", "")}
              </Text>
            </HStack>
          </Pressable>
        ) : null}

        <Pressable
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onPress={onPressDoItMyself}
          disabled={isLoadingDoItMyself || !user || !calendarDayListResponse || !weeklyNutritionPlan}
          style={buttonStyle}
          _pressed={{ opacity: 0.7 }}
          testID="do-it-myself-button"
        >
          <HStack space={2} alignItems="center">
            {isLoadingDoItMyself ? <Spinner size="sm" color="gray.700" /> : null}
            <Text style={textStyle} textAlign="center">
              {t("planner.new_day.generate_empty_day_button_text").replace("!", "")}
            </Text>
          </HStack>
        </Pressable>

        {lastDateThereAreMealsPlannedWithThisNutritionDayPlan ? (
          <Pressable
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onPress={onPressCopyFromDate}
            disabled={isLoadingCopyFromDate || !weeklyNutritionPlan || !nutritionPlanForCurrentDay || !user}
            style={buttonStyle}
            _pressed={{ opacity: 0.7 }}
            testID="copy-from-date-button"
          >
            <HStack space={2} alignItems="center">
              {isLoadingCopyFromDate ? <Spinner size="sm" color="gray.700" /> : null}
              <Text style={textStyle} textAlign="center">
                {t("planner.new_day.copy_from_day_button_text", {
                  day: getLocalizedDay(lastDateThereAreMealsPlannedWithThisNutritionDayPlan.date, userLocaleFromStore),
                })}
              </Text>
            </HStack>
          </Pressable>
        ) : null}
      </VStack>
    </Box>
  );
};

export default EmptyDayActions;
